import axios from "axios";
import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { Card, Table, Alert, Spinner, Button, Modal } from "react-bootstrap";
import { FaEdit, FaTrash, FaMapPin } from "react-icons/fa";
import { MdManageAccounts } from "react-icons/md";
import AddEditSitesModal from "../components/modals/AddEditSitesModal";
import ConfirmModal from "../components/modals/ConfirmModal";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function Sites() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [showAddEditSitesModal, setShowAddEditSitesModal] = useState(false);
  const [editingData, setEditingData] = useState(null);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [plantData, setPlantData] = useState(null);

  // Determine which type based on current route
  const getSiteType = () => {
    if (location.pathname.includes("branches")) return "branch";
    if (location.pathname.includes("plants")) return "plant";
  };

  const type = getSiteType();

  // Get display names based on type
  const getDisplayName = () => {
    if (type === "branch") return "Branch";
    if (type === "plant") return "Plant";
  };

  const displayName = getDisplayName();

  // Format address as comma-separated string from multiple fields
  const formatAddress = (item) => {
    const addressParts = [
      item.address_line_1,
      item.address_line_2,
      item.city,
      item.state,
      item.country,
    ].filter((part) => part && part.trim());

    return addressParts.length > 0 ? addressParts.join(", ") : "N/A";
  };

  // Fetch data based on type (branches, plants)
  const fetchData = useCallback(async () => {
    if (!userData?.company_code) {
      setError("Company code not available");
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${BASE_URL}/api/admin/sites/get-all-sites-based-on-type`,
        {
          params: {
            company_code: userData.company_code,
            type: type,
          },
        },
      );

      if (response.data.success) {
        setItems(response.data.items || []);
      } else {
        setError(response.data.message || `Failed to fetch ${type}s`);
      }
    } catch (err) {
      console.error(`Error fetching ${type}s:`, err);
      setError(err.response?.data?.message || `Error fetching ${type}s`);
    } finally {
      setLoading(false);
    }
  }, [type, userData?.company_code]);

  // Delete item
  const confirmDeleteItem = async () => {
    try {
      const response = await axios.delete(
        `${BASE_URL}/api/admin/sites/delete-site/${itemToDelete}`,
        {
          params: {
            company_code: userData.company_code,
            type: type,
          },
        },
      );

      if (response.data.success) {
        fetchData();
      } else {
        setError(response.data.message || `Failed to delete ${type}`);
      }
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
      setError(err.response?.data?.message || `Error deleting ${type}`);
    }
    setShowConfirmDelete(false);
    setItemToDelete(null);
  };

  const handleEdit = (item) => {
    setEditingData(item);
    setShowAddEditSitesModal(true);
  };

  const handleCloseModal = () => {
    setShowAddEditSitesModal(false);
    setEditingData(null);
  };

  const handleSuccess = () => {
    setShowAddEditSitesModal(false);
    setEditingData(null);
    fetchData();
  };

  const handleManageManagers = (siteId) => {
    if (type === "branch") {
      navigate(`/manage-branch-managers/${siteId}`);
    } else if (type === "plant") {
      navigate(`/manage-plant-managers/${siteId}`);
    }
  };

  const handleManageLocations = (plantId) => {
    // Find the plant data from the items array
    const selectedPlant = items.find((item) => item.site_id === plantId);

    navigate(`/manage-locations/${plantId}`, {
      state: {
        plantData: selectedPlant || plantData,
        siteCode: selectedPlant?.site_code,
      },
    });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="admin-container mt-4">
      <div className="style-header">
        <h1>{displayName} Management</h1>
      </div>
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Card className="mb-4">
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h3 className="loc-titles">{displayName}s List</h3>
            <Button onClick={() => setShowAddEditSitesModal(true)}>
              Add {displayName}
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center p-4">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : items.length === 0 ? (
            <Alert variant="info">No {type}s found</Alert>
          ) : (
            <Table responsive striped bordered hover>
              <thead>
                <tr>
                  <th>{displayName} Name</th>
                  <th>{displayName} Code</th>
                  {type === "branch" && (
                    <>
                      <th>Plant</th>
                    </>
                  )}
                  <th>Address</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.site_id}>
                    <td>{item.site_name}</td>
                    <td>{item.site_code || "Site Code Not Found"}</td>
                    {type === "branch" && (
                      <>
                        <td>{item.plant_name || "N/A"}</td>
                      </>
                    )}
                    <td>{formatAddress(item)}</td>
                    <td>
                      {item.country_code && item.phone
                        ? `${item.country_code}${item.phone}`
                        : item.phone || "N/A"}
                    </td>
                    <td>{item.email || "N/A"}</td>
                    <td>
                      {item.description || "No Additional Information Provided"}
                    </td>
                    <td>
                      <div className="d-flex gap-2">
                        <FaEdit
                          className="action-icon mt-1"
                          onClick={() => handleEdit(item)}
                          title="Edit"
                          style={{ cursor: "pointer", color: "#007bff" }}
                        />
                        <MdManageAccounts
                          className="action-icon"
                          onClick={() => handleManageManagers(item.site_id)}
                          title={`${displayName} Managers`}
                          style={{
                            cursor: "pointer",
                            color: "#007bff",
                            fontSize: "18px",
                          }}
                        />
                        {type === "plant" && (
                          <FaMapPin
                            className="action-icon mt-1"
                            onClick={() => {
                              handleManageLocations(item.site_id, item);
                            }}
                            title="Manage Locations"
                            style={{
                              cursor: "pointer",
                              color: "#28a745",
                              fontSize: "16px",
                            }}
                          />
                        )}
                        <FaTrash
                          className="action-icon text-danger mt-1"
                          onClick={() => {
                            setItemToDelete(item.site_id);
                            setShowConfirmDelete(true);
                          }}
                          title="Delete"
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Add Edit Sites Modal */}
      {showAddEditSitesModal && (
        <Modal
          show={showAddEditSitesModal}
          onHide={handleCloseModal}
          dialogClassName="vcm-modal-dialog"
          centered
        >
          <Modal.Header className="vcm-modal-header" closeButton>
            <Modal.Title>
              {editingData ? `Update ${displayName}` : `Add ${displayName}`}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddEditSitesModal
              editData={editingData}
              onSuccess={handleSuccess}
              type={type}
            />
          </Modal.Body>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        show={showConfirmDelete}
        onHide={() => {
          setShowConfirmDelete(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDeleteItem}
        title={`Delete ${displayName}`}
        message={`Are you sure you want to delete this ${type}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        loading={loading}
      />
    </div>
  );
}